import type { ReactNode } from 'react';
import {
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  SendOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { useTenantBlocked, type PlatformContacts } from '@/settings/hooks';

// M5.7 — постоянный баннер «Работа приостановлена» для заблокированной оптики.
// Рендерится над контентом на всех страницах тенантской админки (AppLayout).
// При is_active !== false возвращает null (обычный режим).

type ContactType = keyof PlatformContacts;

interface ContactPill {
  type: ContactType;
  href: string;
  icon: ReactNode;
  value: string;
}

// Только цифры (и ведущий '+') — для tel:/wa.me.
function digits(value: string): string {
  return value.replace(/[^\d]/g, '');
}

function isUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

function buildPills(contacts: PlatformContacts): ContactPill[] {
  const pills: ContactPill[] = [];

  if (contacts.phone) {
    const raw = contacts.phone;
    pills.push({
      type: 'phone',
      href: `tel:${raw.replace(/[^\d+]/g, '')}`,
      icon: <PhoneOutlined />,
      value: raw,
    });
  }
  if (contacts.email) {
    pills.push({
      type: 'email',
      href: `mailto:${contacts.email}`,
      icon: <MailOutlined />,
      value: contacts.email,
    });
  }
  if (contacts.telegram) {
    const raw = contacts.telegram;
    const href = raw.startsWith('@')
      ? `https://t.me/${raw.slice(1)}`
      : isUrl(raw)
        ? raw
        : `https://t.me/${raw.replace(/^\/+/, '')}`;
    pills.push({ type: 'telegram', href, icon: <SendOutlined />, value: raw });
  }
  if (contacts.whatsapp) {
    const raw = contacts.whatsapp;
    const href = isUrl(raw) ? raw : `https://wa.me/${digits(raw)}`;
    pills.push({ type: 'whatsapp', href, icon: <WhatsAppOutlined />, value: raw });
  }

  return pills;
}

export function BlockedBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const { isBlocked, contacts } = useTenantBlocked();

  if (!isBlocked) return null;

  const pills = contacts ? buildPills(contacts) : [];

  return (
    <div className="sl-blocked-banner" role="alert">
      <div className="sl-blocked-banner__icon" aria-hidden>
        <LockOutlined />
      </div>
      <div className="sl-blocked-banner__text">
        <div className="sl-blocked-banner__title">{t('blocked.title')}</div>
        <div className="sl-blocked-banner__subtitle">{t('blocked.subtitle')}</div>
      </div>
      {pills.length > 0 && (
        <div className="sl-blocked-banner__contacts">
          {pills.map((pill) => (
            <a
              key={pill.type}
              className="sl-blocked-pill"
              href={pill.href}
              target="_blank"
              rel="noreferrer"
              title={t(`blocked.contacts.${pill.type}`)}
            >
              {pill.icon}
              <span>{pill.value}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
